// TEMPORARY mock for the Attendance module — used until the backend
// (/api/attendance/*) is built. Flip ATTENDANCE_USE_MOCK to false once the real
// endpoints exist; the hooks in hooks/queries.js then call the API instead and
// no page needs to change.
//
// The data is generated deterministically (same output on every reload) from
// the 10 real employee codes + device serial seen on the SmartOffice server.
// Names / departments are placeholders.
//
// ---------------------------------------------------------------------------
// API CONTRACT the backend must return (the mock follows it exactly):
//
// GET  /attendance/today
//   → { date, lastSyncAt, rules, summary, rows: DailyRow[], recentPunches: Punch[] }
// GET  /attendance/daily?date=YYYY-MM-DD
//   → { date, rules, summary, rows: DailyRow[] }
// GET  /attendance/monthly?month=YYYY-MM
//   → { month, days: DayMeta[], employees: [{ employeeCode, name, department,
//        days: { "YYYY-MM-DD": DailyRow }, totals: Totals }] }
// GET  /attendance/employee/:code?month=YYYY-MM
//   → { employee, month, days: (DailyRow & { punches: string[] })[], totals }
// POST /attendance/sync
//   → { ok, fetched, inserted, lastSyncAt }
//
// DailyRow = { employeeCode, name, department, date, firstIn, lastOut,
//              punchCount, workMinutes, lateMinutes, earlyMinutes, status,
//              isLate, isEarly, offDay }
//   firstIn / lastOut: "YYYY-MM-DD HH:mm:ss" | null
//   status: present | half_day | absent | missed_punch | in_office | not_in
//           | weekly_off | holiday | null (future date)
// Punch    = { employeeCode, name, punchTime, serialNumber }
// DayMeta  = { date, day, dow, weeklyOff, holiday }   (holiday = name | null)
// Totals   = { present, halfDay, absent, missedPunch, late, weeklyOff,
//              holiday, workMinutes, payableDays }
// Summary  = { total, present, halfDay, absent, late, inOffice, missedPunch,
//              notIn, offDay }
// ---------------------------------------------------------------------------

export const ATTENDANCE_USE_MOCK = false;

// Default rules (will come from the attendance_settings table later).
export const ATTENDANCE_RULES = {
  shiftStart: "10:00",
  shiftEnd: "19:00",
  graceMinutes: 15,
  fullDayMinutes: 7 * 60,
  halfDayMinutes: 4 * 60,
  weeklyOff: [0], // 0 = Sunday
  duplicateWindowMinutes: 2,
};

const DEVICE = "C2653C158F1B1431";
const DATA_START = "2026-10-01";
const DEPARTMENTS = ["Development", "Design", "Support", "Accounts"];
const EMPLOYEES = [7, 8, 9, 10, 11, 12, 14, 15, 16, 18].map((c, i) => ({
  employeeCode: String(c),
  name: `Employee ${c}`,
  department: DEPARTMENTS[i % DEPARTMENTS.length],
}));
const HOLIDAYS = { "2026-11-09": "Diwali (sample)" };

let lastSyncAt = new Date(Date.now() - 6 * 60 * 1000);

// --- date helpers (local time) ----------------------------------------------
const pad = (n) => String(n).padStart(2, "0");
export const ymd = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseYmd = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toStamp = (date, minutes, seconds = 0) =>
  `${date} ${pad(Math.floor(minutes / 60))}:${pad(Math.floor(minutes % 60))}:${pad(seconds)}`;
const stampMinutes = (s) => {
  const [h, m, sec] = s.slice(11).split(":").map(Number);
  return h * 60 + m + (sec || 0) / 60;
};
const hm = (s) => {
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
};

// Small seeded PRNG so a given employee+date always yields the same punches.
function rng(seedStr) {
  let h = 2166136261;
  for (let i = 0; i < seedStr.length; i++) {
    h ^= seedStr.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Raw punches for one employee on one date (includes realistic repeat taps).
function rawPunches(emp, date, now) {
  if (date < DATA_START || date > ymd(now)) return [];
  const dow = parseYmd(date).getDay();
  if (ATTENDANCE_RULES.weeklyOff.includes(dow) || HOLIDAYS[date]) return [];
  const r = rng(`${emp.employeeCode}|${date}`);
  const roll = r();
  if (roll < 0.07) return []; // absent

  const inMin = 9 * 60 + 40 + Math.floor(r() * 55); // 09:40 – 10:35
  const punches = [toStamp(date, inMin, Math.floor(r() * 50))];
  if (r() < 0.35) punches.push(toStamp(date, inMin, 52 + Math.floor(r() * 7))); // repeat tap

  if (roll < 0.12) {
    // forgot to punch out
  } else if (roll < 0.2) {
    punches.push(toStamp(date, 14 * 60 + Math.floor(r() * 70), Math.floor(r() * 59))); // half day
  } else {
    punches.push(toStamp(date, 13 * 60 + 30 + Math.floor(r() * 20), 5)); // lunch out
    punches.push(toStamp(date, 14 * 60 + 5 + Math.floor(r() * 20), 10)); // lunch in
    punches.push(toStamp(date, 18 * 60 + 40 + Math.floor(r() * 70), Math.floor(r() * 59)));
  }
  const nowStamp = `${ymd(now)} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  return punches.filter((p) => p <= nowStamp).sort();
}

// Drop repeat taps inside the duplicate window.
function dedupe(punches) {
  const out = [];
  for (const p of punches) {
    const last = out[out.length - 1];
    if (!last || stampMinutes(p) - stampMinutes(last) >= ATTENDANCE_RULES.duplicateWindowMinutes) {
      out.push(p);
    }
  }
  return out;
}

// The rules engine — the backend will implement the same logic.
function computeDay(emp, date, now) {
  const R = ATTENDANCE_RULES;
  const today = ymd(now);
  const dow = parseYmd(date).getDay();
  const weeklyOff = R.weeklyOff.includes(dow);
  const holiday = HOLIDAYS[date] || null;
  const punches = dedupe(rawPunches(emp, date, now));
  const base = {
    employeeCode: emp.employeeCode,
    name: emp.name,
    department: emp.department,
    date,
    firstIn: null,
    lastOut: null,
    punchCount: punches.length,
    workMinutes: 0,
    lateMinutes: 0,
    earlyMinutes: 0,
    isLate: false,
    isEarly: false,
    offDay: Boolean(weeklyOff || holiday),
    status: null,
    punches,
  };

  if (date > today) return base;
  if (punches.length === 0) {
    if (holiday) return { ...base, status: "holiday" };
    if (weeklyOff) return { ...base, status: "weekly_off" };
    return { ...base, status: date === today ? "not_in" : "absent" };
  }

  const firstIn = punches[0];
  const inMin = stampMinutes(firstIn);
  const lateMinutes = Math.max(0, Math.round(inMin - hm(R.shiftStart)));
  const isLate = inMin > hm(R.shiftStart) + R.graceMinutes;

  if (punches.length === 1) {
    return {
      ...base,
      firstIn,
      lateMinutes: isLate ? lateMinutes : 0,
      isLate,
      status: date === today ? "in_office" : "missed_punch",
    };
  }

  const lastOut = punches[punches.length - 1];
  const outMin = stampMinutes(lastOut);
  const workMinutes = Math.round(outMin - inMin);
  const earlyMinutes = Math.max(0, Math.round(hm(R.shiftEnd) - outMin));
  let status = "absent";
  if (workMinutes >= R.fullDayMinutes) status = "present";
  else if (workMinutes >= R.halfDayMinutes) status = "half_day";
  // Today, while the shift is still running, a short day so far is not final —
  // the employee may be at lunch or still working. Show them as "In Office"
  // until shift end; the day is finalised after that.
  const nowMin = now.getHours() * 60 + now.getMinutes();
  if (date === today && nowMin < hm(R.shiftEnd) && status !== "present") {
    status = "in_office";
  }

  return {
    ...base,
    firstIn,
    lastOut,
    workMinutes,
    lateMinutes: isLate ? lateMinutes : 0,
    earlyMinutes,
    isLate,
    isEarly: earlyMinutes > 0,
    status,
  };
}

function summarize(rows) {
  const s = { total: rows.length, present: 0, halfDay: 0, absent: 0, late: 0, inOffice: 0, missedPunch: 0, notIn: 0, offDay: 0 };
  for (const r of rows) {
    if (r.status === "present") s.present++;
    else if (r.status === "half_day") s.halfDay++;
    else if (r.status === "absent") s.absent++;
    else if (r.status === "in_office") s.inOffice++;
    else if (r.status === "missed_punch") s.missedPunch++;
    else if (r.status === "not_in") s.notIn++;
    else if (r.status === "weekly_off" || r.status === "holiday") s.offDay++;
    if (r.isLate) s.late++;
  }
  return s;
}

function totals(days) {
  const t = { present: 0, halfDay: 0, absent: 0, missedPunch: 0, late: 0, weeklyOff: 0, holiday: 0, workMinutes: 0, payableDays: 0 };
  for (const d of days) {
    if (d.status === "present") t.present++;
    if (d.status === "half_day") t.halfDay++;
    if (d.status === "absent") t.absent++;
    if (d.status === "missed_punch") t.missedPunch++;
    if (d.status === "weekly_off") t.weeklyOff++;
    if (d.status === "holiday") t.holiday++;
    if (d.isLate) t.late++;
    t.workMinutes += d.workMinutes || 0;
  }
  t.payableDays = t.present + t.halfDay * 0.5 + t.weeklyOff + t.holiday;
  return t;
}

function monthDays(month) {
  const [y, m] = month.split("-").map(Number);
  const count = new Date(y, m, 0).getDate();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(y, m - 1, i + 1);
    const date = ymd(d);
    return {
      date,
      day: i + 1,
      dow: d.getDay(),
      weeklyOff: ATTENDANCE_RULES.weeklyOff.includes(d.getDay()),
      holiday: HOLIDAYS[date] || null,
    };
  });
}

const stripPunches = ({ punches, ...row }) => row;
const delay = (v) => new Promise((res) => setTimeout(() => res(v), 250));

// --- mock endpoints ---------------------------------------------------------
export const attendanceMock = {
  getToday() {
    const now = new Date();
    const date = ymd(now);
    const full = EMPLOYEES.map((e) => computeDay(e, date, now));
    const recentPunches = full
      .flatMap((r) => r.punches.map((p) => ({ employeeCode: r.employeeCode, name: r.name, punchTime: p, serialNumber: DEVICE })))
      .sort((a, b) => (a.punchTime < b.punchTime ? 1 : -1))
      .slice(0, 20);
    const rows = full.map(stripPunches);
    return delay({ date, lastSyncAt: lastSyncAt.toISOString(), rules: ATTENDANCE_RULES, summary: summarize(rows), rows, recentPunches });
  },

  getDaily(date) {
    const now = new Date();
    const rows = EMPLOYEES.map((e) => stripPunches(computeDay(e, date, now)));
    return delay({ date, rules: ATTENDANCE_RULES, summary: summarize(rows), rows });
  },

  getMonthly(month) {
    const now = new Date();
    const days = monthDays(month);
    const employees = EMPLOYEES.map((e) => {
      const rows = days.map((d) => stripPunches(computeDay(e, d.date, now)));
      return {
        ...e,
        days: Object.fromEntries(rows.map((r) => [r.date, r])),
        totals: totals(rows),
      };
    });
    return delay({ month, days, employees });
  },

  getEmployeeMonth(code, month) {
    const now = new Date();
    const employee = EMPLOYEES.find((e) => e.employeeCode === String(code));
    if (!employee) return Promise.reject(new Error(`Employee ${code} not found`));
    const days = monthDays(month).map((d) => ({ ...computeDay(employee, d.date, now), holidayName: d.holiday }));
    return delay({ employee, month, days, totals: totals(days) });
  },

  sync() {
    lastSyncAt = new Date();
    return delay({ ok: true, fetched: 0, inserted: 0, lastSyncAt: lastSyncAt.toISOString() });
  },
};
